import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Share2, 
  Copy, 
  Check, 
  Loader2, 
  X,
  Mail,
  ExternalLink,
  Eye,
  Users,
  Sparkles,
  ChevronRight,
  BarChart3,
} from 'lucide-react';
import api from '../../utils/axios';

/**
 * UniversalShareButton - World-class, 1-click sharing experience
 * 
 * Design principles:
 * - 1 click to share (link generated immediately on hover/focus)
 * - Fast links to all major platforms
 * - Beautiful social graph integration
 * - Full tracking & analytics visibility
 * 
 * Inspired by ChatGPT's share experience - elegant, instant, viral.
 */
export default function UniversalShareButton({
  savedSearchId,
  feedId,
  title = 'My Research',
  description: _description,
  resultsCount = 0,
  variant = 'default', // 'default' | 'header' | 'prominent' | 'minimal'
  className = '',
  onShareSuccess,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareToken, setShareToken] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [prefetched, setPrefetched] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const effectiveId = savedSearchId || feedId;

  // Pre-fetch share link on hover for instant experience
  const prefetchShareLink = useCallback(async () => {
    if (prefetched || !effectiveId || shareUrl) return;
    
    try {
      // Use the saved-searches endpoint which is more reliable and doesn't have strict rate limits
      const response = await api.post(`/api/saved-searches/${effectiveId}/share-link`, {
        visibility: 'public',
      });

      // Handle response - can come from either endpoint format
      const shareData = response.data.shareLink || response.data;
      if (response.data.success || shareData.shareToken || shareData.slug) {
        const token = shareData.shareToken || shareData.slug;
        const slug = shareData.slug;
        const baseUrl = window.location.origin;
        
        // Build URL from shareLink object or construct from token/slug
        let url;
        if (shareData.shareUrl) {
          url = shareData.shareUrl;
        } else if (slug && token && slug !== token) {
          url = `${baseUrl}/share/${slug}--${token}`;
        } else {
          url = `${baseUrl}/share/${token}`;
        }
        
        setShareUrl(url);
        setShareToken(token);
        setPrefetched(true);

        // Fetch analytics in background
        fetchAnalytics(token);
      }
    } catch (error) {
      console.warn('Failed to prefetch share link:', error);
    }
  }, [effectiveId, prefetched, shareUrl]);

  // Fetch analytics
  const fetchAnalytics = async (token) => {
    try {
      const response = await api.get(`/api/share/${token}/analytics`);
      if (response.data.success) {
        setAnalytics(response.data.analytics);
      }
    } catch (error) {
      // Analytics optional
    }
  };

  // Handle click - generate link if not prefetched, then open
  const handleClick = async (e) => {
    e.stopPropagation();
    
    if (!shareUrl && !isGenerating) {
      setIsGenerating(true);
      await prefetchShareLink();
      setIsGenerating(false);
    }
    
    setIsOpen(true);
  };

  // Copy link
  const handleCopy = async () => {
    if (!shareUrl) return;
    
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    
    // Track copy event
    if (shareToken) {
      try {
        await api.post(`/api/share/${shareToken}/events`, {
          eventType: 'copy_link',
        });
      } catch {}
    }
    
    setTimeout(() => setCopied(false), 2000);
    onShareSuccess?.(shareUrl);
  };

  // Social share URLs
  const socialLinks = shareUrl ? {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`${title} - ${resultsCount} leads curated with AI`)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`I've curated a list of ${resultsCount} leads for you:\n\n${shareUrl}`)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${title}: ${shareUrl}`)}`,
  } : {};

  // Track social click
  const handleSocialClick = async (platform, url) => {
    if (shareToken) {
      try {
        await api.post(`/api/share/${shareToken}/events`, {
          eventType: 'social_share',
          eventData: { platform },
        });
      } catch {}
    }
    window.open(url, '_blank', 'width=600,height=400');
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Variant styles
  const variants = {
    default: {
      button: `
        flex items-center gap-2 px-4 py-2.5 
        bg-gradient-to-r from-[#5a20ff] to-[#6366f1]
        text-white rounded-full font-semibold text-sm
        shadow-lg shadow-[#5a20ff]/25
        hover:shadow-xl hover:shadow-[#5a20ff]/30 hover:-translate-y-0.5
        active:scale-[0.98]
        transition-all duration-200
      `,
      icon: 'w-4 h-4',
      label: 'Share',
    },
    header: {
      button: `
        flex items-center gap-2 px-3.5 py-2
        bg-[#5a20ff] text-white rounded-xl font-semibold text-sm
        hover:bg-[#5a20ff]/90
        transition-all duration-200
      `,
      icon: 'w-4 h-4',
      label: 'Share',
    },
    prominent: {
      button: `
        flex items-center justify-center gap-3 px-6 py-3.5 w-full
        bg-gradient-to-r from-[#5a20ff] via-[#6366f1] to-[#52dcb3]
        text-white rounded-2xl font-bold text-base
        shadow-lg shadow-[#5a20ff]/30
        hover:shadow-xl hover:shadow-[#5a20ff]/40 hover:-translate-y-0.5
        active:scale-[0.99]
        transition-all duration-200
      `,
      icon: 'w-5 h-5',
      label: 'Share This List',
    },
    minimal: {
      button: `
        flex items-center gap-1.5 px-2.5 py-1.5
        text-[#5a20ff] rounded-lg text-sm font-medium
        hover:bg-[#5a20ff]/10
        transition-all duration-200
      `,
      icon: 'w-4 h-4',
      label: 'Share',
    },
  };

  const style = variants[variant] || variants.default;

  return (
    <div className={`relative ${className}`}>
      {/* Main Share Button */}
      <button
        ref={buttonRef}
        onClick={handleClick}
        onMouseEnter={prefetchShareLink}
        onFocus={prefetchShareLink}
        disabled={!effectiveId}
        className={`${style.button} ${!effectiveId ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {isGenerating ? (
          <Loader2 className={`${style.icon} animate-spin`} />
        ) : (
          <Share2 className={style.icon} />
        )}
        <span>{style.label}</span>
        {analytics?.totalViews > 0 && variant !== 'minimal' && (
          <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded-full text-[10px]">
            {analytics.totalViews}
          </span>
        )}
      </button>

      {/* Share Dropdown - Use Portal to escape stacking context from backdrop-blur */}
      {isOpen && buttonRef.current && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed w-80 bg-white rounded-2xl shadow-2xl border border-black/5 overflow-hidden"
          style={{
            zIndex: 9999,
            top: buttonRef.current.getBoundingClientRect().bottom + 8,
            right: Math.max(16, window.innerWidth - buttonRef.current.getBoundingClientRect().right),
          }}
        >
          <style>{`
            @keyframes eq-dropdown-in {
              from { opacity: 0; transform: translateY(-8px) scale(0.96); }
              to { opacity: 1; transform: translateY(0) scale(1); }
            }
          `}</style>

          {/* Header */}
          <div className="relative bg-gradient-to-br from-[#5a20ff] via-[#6366f1] to-[#4f46e5] p-4 text-white">
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/20 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-white/70">Share Link Ready</p>
                <p className="font-semibold truncate max-w-[180px]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {title}
                </p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="flex gap-2 mt-3 text-xs">
              <div className="flex items-center gap-1 px-2 py-1 bg-white/15 rounded-full">
                <Users className="w-3 h-3" />
                <span>{resultsCount} leads</span>
              </div>
              {analytics?.totalViews > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 bg-white/15 rounded-full">
                  <Eye className="w-3 h-3" />
                  <span>{analytics.totalViews} views</span>
                </div>
              )}
            </div>
          </div>

          {/* Copy Link Section */}
          <div className="p-4 border-b border-black/5">
            <label className="block text-[10px] font-semibold text-black/40 uppercase tracking-wide mb-2">
              Share Link
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={shareUrl || 'Generating...'}
                readOnly
                className="flex-1 px-3 py-2.5 bg-black/[0.03] border border-black/5 rounded-xl text-sm text-black/80 font-mono truncate"
              />
              <button
                onClick={handleCopy}
                disabled={!shareUrl}
                className={`
                  px-4 py-2.5 rounded-xl font-semibold text-sm
                  flex items-center gap-2 transition-all
                  ${copied 
                    ? 'bg-[#52dcb3] text-white' 
                    : 'bg-[#5a20ff] text-white hover:bg-[#5a20ff]/90'
                  }
                  disabled:opacity-50
                `}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Fast Share Links */}
          <div className="p-4">
            <label className="block text-[10px] font-semibold text-black/40 uppercase tracking-wide mb-3">
              Share to
            </label>
            <div className="grid grid-cols-5 gap-2">
              <SocialButton 
                icon={<TwitterIcon />}
                label="Twitter"
                color="#1DA1F2"
                onClick={() => handleSocialClick('twitter', socialLinks.twitter)}
                disabled={!shareUrl}
              />
              <SocialButton 
                icon={<LinkedInIcon />}
                label="LinkedIn"
                color="#0077B5"
                onClick={() => handleSocialClick('linkedin', socialLinks.linkedin)}
                disabled={!shareUrl}
              />
              <SocialButton 
                icon={<FacebookIcon />}
                label="Facebook"
                color="#1877F2"
                onClick={() => handleSocialClick('facebook', socialLinks.facebook)}
                disabled={!shareUrl}
              />
              <SocialButton 
                icon={<WhatsAppIcon />}
                label="WhatsApp"
                color="#25D366"
                onClick={() => handleSocialClick('whatsapp', socialLinks.whatsapp)}
                disabled={!shareUrl}
              />
              <SocialButton 
                icon={<Mail className="w-4 h-4" />}
                label="Email"
                color="#EA4335"
                onClick={() => window.location.href = socialLinks.email}
                disabled={!shareUrl}
              />
            </div>
          </div>

          {/* Analytics Preview */}
          {analytics && (analytics.totalViews > 0 || analytics.linkCopies > 0) && (
            <div className="px-4 pb-4">
              <div className="p-3 bg-gradient-to-r from-[#5a20ff]/5 to-[#52dcb3]/5 rounded-xl border border-[#5a20ff]/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-black/60">
                    <BarChart3 className="w-3.5 h-3.5 text-[#5a20ff]" />
                    <span>{analytics.totalViews || 0} views</span>
                    <span className="text-black/20">•</span>
                    <span>{analytics.linkCopies || 0} shares</span>
                    {analytics.signInClicks > 0 && (
                      <>
                        <span className="text-black/20">•</span>
                        <span className="text-[#52dcb3]">{analytics.signInClicks} sign-ups</span>
                      </>
                    )}
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-black/30" />
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="px-4 py-3 bg-black/[0.02] border-t border-black/5 flex items-center justify-between">
            <div className="flex items-center gap-2 text-[10px] text-black/40">
              <div className="w-3 h-3 rounded bg-gradient-to-br from-[#5a20ff] to-[#52dcb3]" />
              <span>Recipients can preview & sign up</span>
            </div>
            <a 
              href={shareUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-[#5a20ff] hover:underline"
            >
              Preview <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Social button component
function SocialButton({ icon, label, color, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-black/5 transition-colors disabled:opacity-50 group"
      title={label}
    >
      <div 
        className="w-9 h-9 rounded-full flex items-center justify-center text-white transition-transform group-hover:scale-110"
        style={{ backgroundColor: color }}
      >
        {icon}
      </div>
      <span className="text-[10px] text-black/50">{label}</span>
    </button>
  );
}

// Custom SVG icons for social platforms
const TwitterIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);
